import {Route, Switch, useHistory, useLocation} from "react-router-dom";
import React, {useCallback, useMemo} from "react";
import {matchPath} from "react-router";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {Col} from "reactstrap";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import TaxManagementListView from "./tax-management/list";
import TaxManagementRegionTaxesView from "./tax-management/region-taxes";
import SystemRegionsListView from "./regions";
import IndustryManagementView from "./industry-management";
import PriceLevelConfigurationsView from "./price-levels";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import SalesPersonsAccessLevelsView from "./sales-persons-access-levels";
import PersonalConfigurationsView from "./configurations/personal";
import OrganizationalConfigurationsView from "./configurations/organizational";
import ProductPropertiesConfigurationsView from "./product-properties";
import TerritoryManagementView from "./territory-management";

const tabs = {
    taxesList: 'taxesList',
    defaultProvincialTaxes: 'defaultProvincialTaxes',
    regions: 'regions',
    configurations: 'configurations',
    personalConfigurations: 'personalConfigurations',
    industries: 'industries',
    priceLevels: 'priceLevels',
    salesPersons: 'salesPersons',
    productProperties: 'productProperties',
    territories: 'territories',
};

const defaultTab = tabs.configurations;

const SystemSettingsView = () => {
    const location = useLocation();
    const history = useHistory();
    const operatorAccessLevels = useAccessLevels();


    const tabsList = useMemo(() => {
        let res = [
            {
                title: "Organization Configs",
                value: tabs.configurations,
            },
            {
                title: "Personal Configs",
                value: tabs.personalConfigurations,
            },
            {
                title: "Industries",
                value: tabs.industries,
            },
            {
                title: "Taxes List",
                value: tabs.taxesList,
            },
            {
                title: "Default Provincial Taxes",
                value: tabs.defaultProvincialTaxes,
            },
            {
                title: "Regions",
                value: tabs.regions,
            },
            {
                title: "Price Levels",
                value: tabs.priceLevels,
            },
            {
                title: "Product Properties",
                value: tabs.productProperties,
            },
            {
                title: "Sales Persons",
                value: tabs.salesPersons
            },
            {
                title: "Territories",
                value: tabs.territories,
            },
        ];
        if (!operatorAccessLevels.packages.base.organizationSettings) {
            res = res.filter(tab => tab.value !== tabs.configurations);
        }
        if (!operatorAccessLevels.packages.base.personalSettings) {
            res = res.filter(tab => tab.value !== tabs.personalConfigurations);
        }
        if (!operatorAccessLevels.packages.lookup.manageIndustries) {
            res = res.filter(tab => tab.value !== tabs.industries);
        }
        if (!operatorAccessLevels.packages.base.manageTaxes) {
            res = res.filter(tab => tab.value !== tabs.taxesList);
            res = res.filter(tab => tab.value !== tabs.defaultProvincialTaxes);
        }
        if (!operatorAccessLevels.packages.base.manageRegions) {
            res = res.filter(tab => tab.value !== tabs.regions);
        }
        if (!operatorAccessLevels.packages.sales.managePriceLevels) {
            res = res.filter(tab => tab.value !== tabs.priceLevels);
        }
        if (!operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels) {
            res = res.filter(tab => tab.value !== tabs.salesPersons);
        }
        if (!operatorAccessLevels.packages.lookup.manageProductPropertiesSettings) {
            res = res.filter(tab => tab.value !== tabs.productProperties);
        }
        if (!operatorAccessLevels.packages.base.manageTerritories) {
            res = res.filter(tab => tab.value !== tabs.territories);
        }
        return res;
    }, [operatorAccessLevels.packages.base.manageRegions, operatorAccessLevels.packages.base.manageTaxes, operatorAccessLevels.packages.base.manageTerritories, operatorAccessLevels.packages.base.organizationSettings, operatorAccessLevels.packages.base.personalSettings, operatorAccessLevels.packages.lookup.manageIndustries, operatorAccessLevels.packages.lookup.manageProductPropertiesSettings, operatorAccessLevels.packages.sales.managePriceLevels, operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels]);


    const tab = useMemo(() => {
        if (matchPath(location.pathname, {path: routes.settings.taxes.list, exact: true})) {
            return tabs.taxesList;
        }
        if (matchPath(location.pathname, {path: routes.settings.taxes.regionTaxes, exact: true})) {
            return tabs.defaultProvincialTaxes;
        }
        if (matchPath(location.pathname, {path: routes.settings.regions, exact: true})) {
            return tabs.regions;
        }
        if (matchPath(location.pathname, {path: routes.settings.organization, exact: true})) {
            return tabs.configurations;
        }
        if (matchPath(location.pathname, {path: routes.settings.personal, exact: true})) {
            return tabs.personalConfigurations;
        }
        if (matchPath(location.pathname, {path: routes.settings.industries, exact: true})) {
            return tabs.industries;
        }
        if (matchPath(location.pathname, {path: routes.settings.priceLevels, exact: true})) {
            return tabs.priceLevels;
        }
        if (matchPath(location.pathname, {path: routes.settings.salesPersons, exact: true})) {
            return tabs.salesPersons;
        }
        if (matchPath(location.pathname, {path: routes.settings.productProperties, exact: true})) {
            return tabs.productProperties;
        }
        if (matchPath(location.pathname, {path: routes.settings.territories, exact: true})) {
            return tabs.territories;
        }
        return defaultTab;
    }, [location.pathname])

    /**
     * Changes url associated with the [newTab] value when the user clicks on a new tab in the UI.
     * @type {(function(*, *): (undefined|void))}
     */
    const onTabChanged = useCallback((e, newTab) => {
        if (tab === newTab)
            return;
        switch (newTab) {
            case tabs.taxesList:
                return history.push(routes.settings.taxes.list)
            case tabs.defaultProvincialTaxes:
                return history.push(routes.settings.taxes.regionTaxes)
            case tabs.regions:
                return history.push(routes.settings.regions)
            case tabs.configurations:
                return history.push(routes.settings.organization)
            case tabs.personalConfigurations:
                return history.push(routes.settings.personal)
            case tabs.industries:
                return history.push(routes.settings.industries)
            case tabs.priceLevels:
                return history.push(routes.settings.priceLevels)
            case tabs.salesPersons:
                return history.push(routes.settings.salesPersons)
            case tabs.productProperties:
                return history.push(routes.settings.productProperties)
            case tabs.territories:
                return history.push(routes.settings.territories)
            default:
                return;
        }
    }, [history, tab])

    return (
        <>
            <div className={'d-flex flex-wrap w-100'}>
                <Col xs={12} className={''}>
                    <p className={'text-lg font-weight-semi-bold'}>
                        System Settings
                    </p>
                </Col>
                <Col xs={12}>
                    <Tabs className={'my-4'} value={tab ?? false} onChange={onTabChanged}>
                        {
                            tabsList?.map((tab) => (
                                <Tab
                                    key={tab.value}
                                    value={tab.value}
                                    label={tab.title}
                                />
                            ))
                        }
                    </Tabs>
                </Col>
                <Col xs={12}>
                    <Switch>
                        {
                            operatorAccessLevels.packages.base.organizationSettings &&
                            <Route path={routes.settings.organization} exact>
                                <OrganizationalConfigurationsView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.base.personalSettings &&
                            <Route path={routes.settings.personal} exact>
                                <PersonalConfigurationsView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.base.manageTaxes &&
                            <Route path={routes.settings.taxes.list} exact>
                                <TaxManagementListView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.base.manageTaxes &&
                            <Route path={routes.settings.taxes.regionTaxes} exact>
                                <TaxManagementRegionTaxesView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.base.manageRegions &&
                            <Route path={routes.settings.regions} exact>
                                <SystemRegionsListView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.lookup.manageIndustries &&
                            <Route path={routes.settings.industries} exact>
                                <IndustryManagementView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.sales.managePriceLevels &&
                            <Route path={routes.settings.priceLevels} exact>
                                <PriceLevelConfigurationsView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels &&
                            <Route path={routes.settings.salesPersons} exact>
                                <SalesPersonsAccessLevelsView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.lookup.manageProductPropertiesSettings &&
                            <Route path={routes.settings.productProperties} exact>
                                <ProductPropertiesConfigurationsView/>
                            </Route>
                        }
                        {
                            operatorAccessLevels.packages.base.manageTerritories &&
                            <Route path={routes.settings.territories} exact>
                                <TerritoryManagementView/>
                            </Route>
                        }
                    </Switch>
                </Col>
            </div>
        </>
    )
}

export default SystemSettingsView;
